import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import LotusLoader from "../components/LotusLoader";
import Particles from "../components/Particles";
import SacredReport from "../components/SacredReport";
import { ordersApi, customerApi } from "../lib/api";
import { getSession, clearSession, touchSession } from "../lib/customerSession";
import { BRAND } from "../lib/constants";

export default function MyPrescriptionDashboard() {
  const navigate = useNavigate();
  const session = getSession();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    if (!session?.orderId) {
      navigate("/my-prescription", { replace: true });
      return;
    }
    Promise.all([
      ordersApi.get(session.orderId),
      customerApi.suggestions(session.orderId).catch(() => []),
      customerApi.reports(session.orderId).catch(() => []),
    ])
      .then(([o, sug, rep]) => {
        setOrder(o);
        setSuggestions(Array.isArray(sug) ? sug : []);
        setReports(Array.isArray(rep) ? rep : []);
        setLoading(false);
        touchSession();
        customerApi.logActivity(session.orderId, "dashboard_overview").catch(() => {});
      })
      .catch(() => {
        clearSession();
        navigate("/my-prescription", { replace: true });
      });
  }, [session?.orderId, navigate]);

  const logout = () => {
    clearSession();
    navigate("/my-prescription", { replace: true });
  };

  if (loading) return <LotusLoader context="general" />;
  if (!order) return null;

  const firstName = (order.fullName || "").split(" ")[0] || "Friend";
  const waUrl = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(`Namaste, this is regarding order #${order.orderNumber}.`)}`;

  return (
    <div className="min-h-screen" data-testid="my-prescription-dashboard">
      <BrandHeader />

      {/* Hero */}
      <section className="relative overflow-hidden hero-mystical">
        <Particles variant="violet" />
        <div className="relative max-w-4xl mx-auto px-5 md:px-10 py-12 md:py-16 text-center">
          <div className="ph-overline" style={{ color: "#D4A855" }}>Your Sacred Dashboard</div>
          <h1 className="font-serif text-3xl md:text-5xl mt-3 text-white" data-testid="dashboard-headline">
            Welcome back, {firstName} ✦
          </h1>
          <p className="font-serif italic text-base md:text-lg mt-4 max-w-xl mx-auto" style={{ color: "#FAF7F2" }}>
            Your prescription is yours to revisit, anytime.
          </p>
          <div className="inline-flex flex-wrap items-center justify-center gap-3 mt-5 text-xs tracking-[0.2em] uppercase" style={{ color: "#D4A855" }}>
            <span>Order #{order.orderNumber}</span>
            <span>·</span>
            <span>{order.status === "paid" ? "Paid" : "Pending"}</span>
            <span>·</span>
            <button
              onClick={logout}
              className="hover:text-white transition"
              data-testid="logout-btn"
            >
              Logout
            </button>
          </div>
        </div>
      </section>

      {/* Quick Glossary — one-time explainer of the technical terms customers meet across the report */}
      <section className="bg-[#FAF7F2] pt-8 pb-2" data-testid="quick-glossary">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <details className="rounded-2xl" style={{ background: "linear-gradient(135deg, #FAF8F4 0%, #F5F0E8 100%)", border: "1px solid rgba(212,168,85,0.35)" }}>
            <summary className="px-5 py-4 cursor-pointer select-none flex items-center justify-between" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", color: "#2C0A5E" }} data-testid="quick-glossary-toggle">
              <span>Quick Glossary <span style={{ fontStyle: "italic", fontSize: "13px", color: "#6B5E7E", fontFamily: "Georgia, serif" }}>&mdash; new to the terms? open this once, you&rsquo;re done</span></span>
              <span style={{ fontSize: "18px", color: "#D4A855" }}>&#9662;</span>
            </summary>
            <div className="px-5 pb-5 pt-1">
              <ul style={{ margin: 0, padding: 0, listStyle: "none", fontSize: "14px", lineHeight: 1.7, color: "#1A1030", fontFamily: "Georgia, serif" }}>
                <li style={{ marginBottom: "10px" }}><strong>Rahu &amp; Ketu</strong> &mdash; not real planets, can&rsquo;t see them in the sky. Two invisible points in Vedic astrology that pull your energy in different directions. Rahu pulls you toward new things &mdash; ambition, hunger, wanting more. Ketu pulls you toward old wisdom you already carry.</li>
                <li style={{ marginBottom: "10px" }}><strong>Life Path Number</strong> &mdash; add up your birthday&rsquo;s numbers, get one number. The main lesson your life is teaching you.</li>
                <li style={{ marginBottom: "10px" }}><strong>Name Number</strong> &mdash; same idea, using your name. How people &ldquo;feel&rdquo; you the moment they meet you.</li>
                <li style={{ marginBottom: "10px" }}><strong>Personal Year</strong> &mdash; your life runs on a 9-year loop. This tells you which part of the loop you&rsquo;re on.</li>
                <li style={{ marginBottom: "10px" }}><strong>Chakra</strong> &mdash; one of 7 energy points in your body, each linked to something different.</li>
                <li style={{ marginBottom: "10px" }}><strong>Bija Mantra</strong> &mdash; a one-word sound you chant to switch on a chakra.</li>
                <li><strong>Exaltation</strong> &mdash; astrologers&rsquo; way of saying a planet is having its best day.</li>
              </ul>
            </div>
          </details>
        </div>
      </section>

      {/* Personalisation strip — suggestion engine drives this. */}
      <section className="bg-[#FAF7F2] py-8" data-testid="personalisation-strip">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          {suggestions.length === 0 ? (
            <div className="text-center text-sm text-[#6B5E7E] italic">
              No active recommendations right now — explore your full prescription below.
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {suggestions.map((s) => (
                <SuggestionCard key={`${s.priority}-${s.kind}-${s.headline}`} card={s} orderId={order.id} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Delivery snapshot */}
      {order.deliveryAddress?.pincode && (
        <section className="bg-[#FAF7F2] pb-6">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            <div className="ph-card p-5" data-testid="delivery-snapshot">
              <div className="ph-overline">Delivery</div>
              <p className="text-sm text-[#2C0A5E] mt-2">
                <strong>{order.deliveryAddress.fullName}</strong> · {order.deliveryAddress.city}, {order.deliveryAddress.state} {order.deliveryAddress.pincode}
              </p>
              <Link
                to={`/order/${order.id}/address`}
                className="inline-block mt-2 text-xs tracking-[0.2em] uppercase"
                style={{ color: "#6A0DAD" }}
                data-testid="edit-address-link"
              >
                Edit address →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Monthly Reports archive */}
      {reports.length > 0 && (
        <section className="bg-[#FAF7F2] pb-2">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            <div className="ph-card p-5" data-testid="reports-archive">
              <div className="ph-overline">Your Monthly Reports</div>
              <ul className="mt-3 divide-y divide-[rgba(106,13,173,0.08)]">
                {reports.map((r) => {
                  const downloadUrl = customerApi.reportDownloadUrl(order.id, r.id);
                  const phoneDigits = (order.whatsapp || "").replace(/\D/g, "");
                  const waText = `My Pancha Healing report for ${r.monthName} ${r.year} ✦ ${downloadUrl}`;
                  const sendToSelfUrl = phoneDigits
                    ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(waText)}`
                    : `https://wa.me/?text=${encodeURIComponent(waText)}`;
                  return (
                    <li
                      key={r.id}
                      className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                    >
                      <span className="text-sm text-[#2C0A5E]">
                        <strong>{r.monthName} {r.year}</strong>
                        <span className="text-[#6B5E7E] ml-2 text-xs">
                          · {r.rowsIncluded} window{r.rowsIncluded !== 1 ? "s" : ""} relevant to you
                        </span>
                      </span>
                      <span className="flex items-center gap-4">
                        <a
                          href={sendToSelfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs tracking-[0.2em] uppercase inline-flex items-center gap-1.5"
                          style={{ color: "#1DA851" }}
                          data-testid={`report-whatsapp-${r.year}-${r.month}`}
                          aria-label={`Send the ${r.monthName} ${r.year} report on WhatsApp`}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                            <path d="M20.52 3.48A11.85 11.85 0 0 0 12.06 0C5.5 0 .2 5.3.2 11.85c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.86 11.86 0 0 0 5.66 1.44h.01c6.55 0 11.85-5.3 11.85-11.85 0-3.16-1.23-6.13-3.4-8.43zM12.07 21.6h-.01a9.8 9.8 0 0 1-4.99-1.37l-.36-.21-3.8 1 1.02-3.7-.23-.38a9.79 9.79 0 0 1 14.99-12.4 9.72 9.72 0 0 1 2.86 6.92c0 5.41-4.4 9.8-9.81 9.8z" />
                          </svg>
                          Send on WhatsApp
                        </a>
                        <a
                          href={downloadUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs tracking-[0.2em] uppercase"
                          style={{ color: "#6A0DAD" }}
                          data-testid={`report-download-${r.year}-${r.month}`}
                        >
                          Download PDF →
                        </a>
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className="text-[11px] text-[#6B5E7E] italic mt-3">
                Sending on WhatsApp opens a chat with your own number — you can keep the link in your messages or forward it to a friend.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Sacred Report (15 sections) */}
      <section className="bg-[#FAF7F2] pb-14 md:pb-20">
        <div className="max-w-5xl mx-auto px-3 md:px-8">
          <SacredReport order={order} />
        </div>
      </section>

      {/* WhatsApp footer */}
      <section className="bg-[#FAF7F2] pb-12">
        <div className="max-w-2xl mx-auto px-5 md:px-10 text-center">
          <p className="text-sm text-[#6B5E7E] mb-3">
            Questions? Want guidance on wearing your bracelet?
          </p>
          <a
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            className="ph-cta"
            data-testid="dashboard-whatsapp"
          >
            Chat with us on WhatsApp
            <span className="arrow" aria-hidden>→</span>
          </a>
        </div>
      </section>
    </div>
  );
}

function SuggestionCard({ card }) {
  const accent = card.priority === 1 ? "#D4A855" : card.priority === 2 ? "#6A0DAD" : "#8B47D9";
  return (
    <div
      className="rounded-xl p-5 text-left"
      style={{
        background: "white",
        border: `1px solid ${accent}33`,
        borderTop: `3px solid ${accent}`,
        boxShadow: "0 4px 14px rgba(106,13,173,0.06)",
      }}
      data-testid={`suggestion-card-p${card.priority}`}
    >
      <div className="ph-overline" style={{ color: accent }}>{card.title}</div>
      <div
        className="font-serif text-[#2C0A5E] mt-2"
        style={{ fontSize: "17px", lineHeight: 1.3 }}
        data-testid="suggestion-headline"
      >
        {card.headline}
      </div>
      <p
        className="text-sm text-[#1A1030] mt-2"
        style={{ fontFamily: "Georgia, serif", lineHeight: 1.6 }}
      >
        {card.body}
      </p>
      {card.meta?.crystal_tip && (
        <p className="text-xs italic mt-2" style={{ color: accent }}>
          ✦ {card.meta.crystal_tip}
        </p>
      )}
      {card.meta?.chakra && (
        <p className="text-xs italic mt-2" style={{ color: "#6B5E7E" }}>
          Linked chakra: {card.meta.chakra}
        </p>
      )}
      {card.meta?.start_date && card.meta?.end_date && card.meta.start_date !== card.meta.end_date && (
        <p className="text-[10px] tracking-[0.18em] uppercase mt-3" style={{ color: "#6B5E7E", fontFamily: "Jost, sans-serif" }}>
          {card.meta.start_date} — {card.meta.end_date}
        </p>
      )}
    </div>
  );
}
